import { CSSProperties, useEffect, useState } from 'react';
import { Avatar, Badge, Card, Progress, Spin, Table, Tag, Timeline } from 'antd';
import {
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { API } from '../../api';

const colors = {
  accent: '#2563eb',
  accentLt: '#eff4ff',
  text: '#0f1c35',
  muted: '#7a90b0',
  muted2: '#a0b3cc',
  border: '#e2eaf5',
  green: '#0ea66a',
  greenLt: '#ecfdf5',
  red: '#e03a3a',
  redLt: '#fff1f1',
  orange: '#f07c28',
  orangeLt: '#fff7ed',
  sky: '#0ea5e9',
  skyLt: '#f0faff',
};

const REFRESH_INTERVAL_MS = 2 * 60 * 1000;
const WEEKDAY_LABELS = ['Ня', 'Да', 'Мя', 'Лх', 'Пү', 'Ба', 'Бя'];

const money = (value?: number | null) => `₮${(value ?? 0).toLocaleString('mn-MN')}`;

const initials = (name?: string) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2);

const statusBadge = (complete: boolean, reasonRequired: boolean, text: string) => {
  if (complete) return { status: 'success' as const, text };
  if (reasonRequired) return { status: 'error' as const, text };
  return { status: 'processing' as const, text };
};

const buildSparkline = (values: number[], width: number, height: number, pad = 4) => {
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const step = width / (values.length - 1 || 1);
  const points = values.map((v, i) => {
    const x = i * step;
    const y = pad + (1 - (v - min) / range) * (height - pad * 2);
    return [x, y];
  });
  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');
  const area = `${line} L${width},${height} L0,${height} Z`;
  return { line, area, points };
};

const panelStyle: CSSProperties = {
  background: '#fff',
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  boxShadow: '0 1px 4px rgba(15,28,53,0.06), 0 4px 16px rgba(15,28,53,0.05)',
  overflow: 'hidden',
};

const panelHeaderStyle: CSSProperties = {
  padding: '14px 20px',
  borderBottom: `1px solid ${colors.border}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const panelTitleStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1,
  color: colors.muted,
};

const changeTag = (percent?: number | null, featured?: boolean) => {
  if (percent === null || percent === undefined) return null;
  const up = percent >= 0;
  return (
    <Tag
      color={featured ? undefined : up ? 'success' : 'error'}
      style={
        featured
          ? { background: 'rgba(255,255,255,0.18)', color: '#fff', border: 'none' }
          : undefined
      }
    >
      {up ? '▲' : '▼'} {Math.abs(percent)}%
    </Tag>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<any>(null);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await API.get({ apiVersion: 'api' })('/dashboard/summary');
      setSummary(response.result);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    const timer = setInterval(loadSummary, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  if (!summary) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin spinning={loading} />
      </div>
    );
  }

  const weeklyRevenueMillions = summary.weeklyRevenue.map((d: any) => d.amount / 1_000_000);
  const spark = buildSparkline(weeklyRevenueMillions, 260, 56, 6);
  const barMax = Math.max(...weeklyRevenueMillions, 1);

  const donutSegments = [
    { key: 'success', label: 'Хүргэсэн', value: summary.successOrderCount, color: colors.green },
    { key: 'transit', label: 'Замд явна', value: summary.transitOrderCount, color: colors.sky },
    { key: 'failed', label: 'Амжилтгүй', value: summary.failedOrderCount, color: colors.red },
  ].filter((s) => s.value > 0);
  const donutTotal = summary.totalOrderCount || 0;
  const donutR = 54;
  const donutC = 2 * Math.PI * donutR;
  let donutCursor = 0;

  const kpis = [
    {
      key: 'revenue',
      label: 'Өнөөдрийн орлого',
      value: money(summary.todayRevenue),
      icon: <DollarOutlined />,
      footer: 'өчигдрийнхөөс',
      change: summary.revenueChangePercent,
      featured: true,
    },
    {
      key: 'total',
      label: 'Нийт захиалга',
      value: String(summary.totalOrderCount ?? 0),
      icon: <InboxOutlined />,
      color: colors.accent,
      bg: colors.accentLt,
      footer: 'өчигдрийнхөөс',
      change: summary.orderCountChangePercent,
    },
    {
      key: 'success',
      label: 'Амжилттай',
      value: String(summary.successOrderCount ?? 0),
      icon: <CheckCircleOutlined />,
      color: colors.green,
      bg: colors.greenLt,
      footer: 'гүйцэтгэл',
      change: summary.successRate,
    },
    {
      key: 'transit',
      label: 'Замд явна',
      value: String(summary.transitOrderCount ?? 0),
      icon: <CarOutlined />,
      color: colors.sky,
      bg: colors.skyLt,
      footer: 'идэвхтэй жолооч',
      change: summary.activeDriverCount,
      isCount: true,
    },
    {
      key: 'failed',
      label: 'Амжилтгүй',
      value: String(summary.failedOrderCount ?? 0),
      icon: <CloseCircleOutlined />,
      color: colors.red,
      bg: colors.redLt,
      footer: 'бүтэлгүйтлийн хувь',
      change: summary.failedRate,
    },
  ];

  const orderColumns = [
    {
      title: 'Захиалга',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (value: string) => (
        <span style={{ fontFamily: 'monospace', color: colors.accent, fontWeight: 500 }}>
          {value || '—'}
        </span>
      ),
    },
    {
      title: 'Хэрэглэгч',
      key: 'customer',
      render: (record: any) => (
        <div>
          <div style={{ fontWeight: 600, color: colors.text }}>{record.customerName}</div>
          <div style={{ fontSize: 12, color: colors.muted }}>{record.address}</div>
        </div>
      ),
    },
    {
      title: 'Жолооч',
      key: 'driver',
      render: (record: any) =>
        record.driverName ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar
              size={28}
              shape="square"
              style={{
                background: colors.accentLt,
                color: colors.accent,
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {initials(record.driverName)}
            </Avatar>
            {record.driverName}
          </div>
        ) : (
          <span style={{ color: colors.muted2 }}>—</span>
        ),
    },
    {
      title: 'Үнэ',
      key: 'amount',
      render: (record: any) => (
        <span
          style={{
            fontWeight: 500,
            color: record.reasonRequired ? colors.muted2 : colors.text,
            textDecoration: record.reasonRequired ? 'line-through' : 'none',
          }}
        >
          {money(record.amount)}
        </span>
      ),
    },
    {
      title: 'Төлөв',
      key: 'status',
      render: (record: any) => {
        const meta = statusBadge(record.complete, record.reasonRequired, record.statusName);
        return <Badge status={meta.status} text={meta.text} />;
      },
    },
    {
      title: 'Цаг',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => (
        <span style={{ fontSize: 12, color: colors.muted }}>
          {value ? dayjs(value).format('HH:mm') : '—'}
        </span>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* KPI ROW */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {kpis.map((kpi) => (
          <Card
            key={kpi.key}
            variant="borderless"
            style={{
              flex: '1 1 190px',
              minWidth: 190,
              borderRadius: 16,
              background: kpi.featured
                ? `linear-gradient(135deg, ${colors.accent} 0%, #1e40af 100%)`
                : '#fff',
              boxShadow: '0 1px 4px rgba(15,28,53,0.06), 0 4px 16px rgba(15,28,53,0.05)',
            }}
            styles={{ body: { padding: '18px 20px' } }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                marginBottom: 12,
                background: kpi.featured ? 'rgba(255,255,255,0.18)' : kpi.bg,
                color: kpi.featured ? '#fff' : kpi.color,
              }}
            >
              {kpi.icon}
            </div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                color: kpi.featured ? 'rgba(255,255,255,0.65)' : colors.muted,
                marginBottom: 4,
              }}
            >
              {kpi.label}
            </div>
            <div
              style={{
                fontSize: 30,
                fontWeight: 700,
                lineHeight: 1,
                marginBottom: 8,
                color: kpi.featured ? '#fff' : (kpi.color ?? colors.text),
              }}
            >
              {kpi.value}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: kpi.featured ? 'rgba(255,255,255,0.6)' : colors.muted,
              }}
            >
              {kpi.isCount ? (
                <Tag color="processing">{kpi.change ?? 0}</Tag>
              ) : (
                changeTag(kpi.change, kpi.featured)
              )}
              {kpi.footer}
            </div>
          </Card>
        ))}
      </div>

      {/* REVENUE BAND */}
      <div
        style={{
          ...panelStyle,
          padding: '20px 26px',
          display: 'flex',
          alignItems: 'center',
          gap: 32,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: colors.muted,
              marginBottom: 4,
            }}
          >
            Сарын нийт орлого
          </div>
          <div style={{ fontSize: 36, fontWeight: 700, color: colors.accent, lineHeight: 1 }}>
            {money(summary.monthRevenue)}
          </div>
          <div style={{ fontSize: 12, color: colors.muted, marginTop: 6, fontFamily: 'monospace' }}>
            Сарын захиалга: {summary.monthOrderCount ?? 0}
          </div>
        </div>
        <div style={{ width: 1, height: 60, background: colors.border }} />
        {[
          { label: 'Дундаж захиалга', value: money(summary.avgOrderAmount), color: colors.accent },
          { label: 'Өнөөдрийн орлого', value: money(summary.todayRevenue), color: colors.green },
          { label: 'Буцаалтын алдагдал', value: money(summary.returnLoss), color: colors.orange },
        ].map((stat, i) => (
          <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
            {i > 0 && <div style={{ width: 1, height: 60, background: colors.border }} />}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: stat.color, lineHeight: 1 }}>
                {stat.value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: colors.muted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontWeight: 600,
                  marginTop: 4,
                }}
              >
                {stat.label}
              </div>
            </div>
          </div>
        ))}
        <div style={{ width: 1, height: 60, background: colors.border }} />
        <div style={{ flex: 2, minWidth: 220 }}>
          <div
            style={{ fontSize: 11, color: colors.muted, fontFamily: 'monospace', marginBottom: 6 }}
          >
            7 хоногийн орлого (сая ₮)
          </div>
          <svg width="100%" height={56} viewBox="0 0 260 56" preserveAspectRatio="none">
            <defs>
              <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.accent} stopOpacity={0.18} />
                <stop offset="100%" stopColor={colors.accent} stopOpacity={0} />
              </linearGradient>
            </defs>
            <path d={spark.area} fill="url(#sparkGrad)" />
            <path
              d={spark.line}
              fill="none"
              stroke={colors.accent}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {spark.points.map(([x, y], i) => (
              <circle
                key={i}
                cx={x}
                cy={y}
                r={i === spark.points.length - 1 ? 4 : 3}
                fill={colors.accent}
                stroke="#fff"
                strokeWidth={1.5}
              />
            ))}
          </svg>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10,
              color: colors.muted2,
              fontFamily: 'monospace',
              marginTop: 2,
            }}
          >
            {summary.weeklyRevenue.map((d: any) => (
              <span key={d.date}>{WEEKDAY_LABELS[dayjs(d.date).day()]}</span>
            ))}
          </div>
        </div>
      </div>

      {/* MID ROW */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ ...panelStyle, flex: '2 1 560px' }}>
          <div style={panelHeaderStyle}>
            <span style={panelTitleStyle}>Сүүлийн захиалгууд</span>
            <a href="/order/list" style={{ fontSize: 12, color: colors.accent, fontWeight: 600 }}>
              Бүгдийг харах →
            </a>
          </div>
          <Table
            dataSource={summary.recentOrders}
            columns={orderColumns}
            rowKey={(r: any, i) => r.orderNumber ?? i}
            pagination={false}
            size="small"
          />
        </div>

        <div style={{ ...panelStyle, flex: '1 1 320px' }}>
          <div style={panelHeaderStyle}>
            <span style={panelTitleStyle}>Идэвхтэй жолооч</span>
            <a
              href="/settings/branch/delivery"
              style={{ fontSize: 12, color: colors.accent, fontWeight: 600 }}
            >
              Бүгд →
            </a>
          </div>
          <div>
            {summary.drivers.length === 0 && (
              <div style={{ padding: 20, fontSize: 12, color: colors.muted }}>
                Өнөөдөр идэвхтэй жолооч алга байна
              </div>
            )}
            {summary.drivers.map((driver: any) => (
              <div
                key={driver.no}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '13px 20px',
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <Avatar
                  size={38}
                  shape="square"
                  style={{
                    background: colors.accentLt,
                    color: colors.accent,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {initials(driver.name)}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>
                    {driver.name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.muted,
                      fontFamily: 'monospace',
                      marginTop: 2,
                    }}
                  >
                    {driver.branchName || '—'}
                  </div>
                  <Progress
                    percent={driver.percent}
                    showInfo={false}
                    size="small"
                    strokeColor={colors.accent}
                    trailColor={colors.border}
                    style={{ marginTop: 6, maxWidth: 140 }}
                  />
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: colors.text }}>
                    {driver.totalCount}
                  </div>
                  <div style={{ fontSize: 11, color: colors.muted, fontFamily: 'monospace' }}>
                    {driver.percent}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* BOTTOM ROW */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {/* BAR CHART */}
        <div style={{ ...panelStyle, flex: '3 1 420px' }}>
          <div style={panelHeaderStyle}>
            <span style={panelTitleStyle}>Орлогын график (сая ₮)</span>
          </div>
          <div style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 11,
                  color: colors.muted,
                  fontFamily: 'monospace',
                }}
              >
                <div style={{ width: 8, height: 8, borderRadius: 2, background: colors.accent }} />
                Орлого
              </div>
            </div>
            <svg width="100%" height={150} viewBox="0 0 360 150" preserveAspectRatio="none">
              <defs>
                <linearGradient id="blueBar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.accent} />
                  <stop offset="100%" stopColor="#93c5fd" />
                </linearGradient>
              </defs>
              {[0, 37, 74, 111, 148].map((y) => (
                <line
                  key={y}
                  x1={0}
                  y1={y}
                  x2={360}
                  y2={y}
                  stroke={colors.border}
                  strokeWidth={1}
                />
              ))}
              {weeklyRevenueMillions.map((_: number, i: number) => {
                const barW = 40;
                const gap = 12;
                const x = i * (barW + gap) + 4;
                return (
                  <rect
                    key={`t${i}`}
                    x={x}
                    y={18}
                    width={barW}
                    height={130}
                    rx={5}
                    fill="#f0f4fa"
                  />
                );
              })}
              {weeklyRevenueMillions.map((v: number, i: number) => {
                const barW = 40;
                const gap = 12;
                const x = i * (barW + gap) + 4;
                const h = (v / barMax) * 130;
                return (
                  <rect
                    key={`a${i}`}
                    x={x}
                    y={148 - h}
                    width={barW}
                    height={h}
                    rx={5}
                    fill="url(#blueBar)"
                    opacity={i === weeklyRevenueMillions.length - 1 ? 1 : 0.85}
                  />
                );
              })}
            </svg>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                fontSize: 10,
                color: colors.muted2,
                fontFamily: 'monospace',
                marginTop: 6,
              }}
            >
              {summary.weeklyRevenue.map((d: any) => (
                <span key={d.date}>{WEEKDAY_LABELS[dayjs(d.date).day()]}</span>
              ))}
            </div>
          </div>
        </div>

        {/* DONUT */}
        <div style={{ ...panelStyle, flex: '2 1 240px' }}>
          <div style={panelHeaderStyle}>
            <span style={panelTitleStyle}>Захиалгын төлөв</span>
          </div>
          <div
            style={{ padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <svg width={148} height={148} viewBox="0 0 148 148">
              <circle
                cx={74}
                cy={74}
                r={donutR}
                fill="none"
                stroke={colors.border}
                strokeWidth={20}
              />
              {donutSegments.map((seg) => {
                const frac = donutTotal > 0 ? seg.value / donutTotal : 0;
                const len = frac * donutC;
                const el = (
                  <circle
                    key={seg.key}
                    cx={74}
                    cy={74}
                    r={donutR}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={20}
                    strokeDasharray={`${len} ${donutC - len}`}
                    strokeDashoffset={-donutCursor}
                    transform="rotate(-90 74 74)"
                  />
                );
                donutCursor += len;
                return el;
              })}
              <text
                x={74}
                y={69}
                textAnchor="middle"
                fontSize={26}
                fontWeight={700}
                fill={colors.text}
              >
                {donutTotal}
              </text>
              <text x={74} y={86} textAnchor="middle" fontSize={10} fill={colors.muted2}>
                нийт
              </text>
            </svg>
            <div
              style={{
                marginTop: 14,
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 8,
              }}
            >
              {donutSegments.map((seg) => (
                <div
                  key={seg.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    fontSize: 11,
                    color: colors.muted,
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: seg.color,
                      flexShrink: 0,
                    }}
                  />
                  {seg.label} {donutTotal > 0 ? Math.round((seg.value / donutTotal) * 100) : 0}%
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ACTIVITY */}
        <div style={{ ...panelStyle, flex: '2 1 260px' }}>
          <div style={panelHeaderStyle}>
            <span style={panelTitleStyle}>Сүүлийн үйл явдал</span>
          </div>
          <div style={{ padding: '14px 20px 6px' }}>
            {summary.activity.length === 0 ? (
              <div style={{ padding: '6px 0 16px', fontSize: 12, color: colors.muted }}>
                Үйл явдал алга байна
              </div>
            ) : (
              <Timeline
                items={summary.activity.map((a: any, i: number) => {
                  const color = a.complete
                    ? colors.green
                    : a.reasonRequired
                      ? colors.red
                      : colors.sky;
                  return {
                    key: i,
                    color,
                    children: (
                      <div>
                        <div style={{ fontSize: 12, color: colors.muted, lineHeight: 1.5 }}>
                          <strong style={{ color: colors.text }}>{a.orderNumber || '—'}</strong>{' '}
                          {a.statusName} · <strong style={{ color }}>{money(a.amount)}</strong>
                        </div>
                        <div
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 11,
                            color: colors.muted2,
                            marginTop: 2,
                          }}
                        >
                          {dayjs(a.createdAt).format('HH:mm')} · {a.memberName || 'Систем'}
                        </div>
                      </div>
                    ),
                  };
                })}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
